import { useCallback } from 'react';
import { API_BASE } from '../../../config/constants';

export default function useCRM() {
    const fetchInvestors = useCallback(async () => {
        const res = await fetch(`${API_BASE}/api/users/get-investors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch investors');
        return res.json();
    }, []);

    const fetchContacts = useCallback(async () => {
        const res = await fetch(`${API_BASE}/api/users/get-contacts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch contacts');
        return res.json();
    }, []);

    const fetchContactsByInvestor = useCallback(async (investor_id: number | string) => {
        const res = await fetch(`${API_BASE}/api/users/get-contacts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ investor_id }),
        });
        if (!res.ok) throw new Error('Failed to fetch contacts for investor');
        return res.json();
    }, []);

    const fetchPeople = useCallback(async () => {
        const res = await fetch(`${API_BASE}/api/users/get-people`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch people');
        return res.json();
    }, []);

    const listInvestorTypes = useCallback(async () => {
        const res = await fetchInvestors();
        const data = Array.isArray(res) ? res : res?.data ?? [];
        if (!Array.isArray(data)) return [];
        // find a likely key for investor type
        const keys = Object.keys(data[0] ?? {});
        const typeKey = keys.find((k) => /investor\s*type/i.test(k) || /type/i.test(k));
        const values = new Set<string>();
        for (const r of data) {
            const v = typeKey ? r[typeKey] : (r.type ?? r.investor_type ?? r['Investor Type']);
            if (v || v === 0) values.add(String(v).trim());
        }
        return Array.from(values).filter((s) => s.trim() !== '');
    }, [fetchInvestors]);

    const listInvestmentRegions = useCallback(async () => {
        const res = await fetchInvestors();
        const data = Array.isArray(res) ? res : res?.data ?? [];
        if (!Array.isArray(data)) return [];
        const keys = Object.keys(data[0] ?? {});
        const regionKey = keys.find((k) => /investment\s*region/i.test(k) || /region/i.test(k));
        const values = new Set<string>();
        for (const r of data) {
            const v = regionKey ? r[regionKey] : (r.region ?? r.investment_region ?? r['Investment Region']);
            if (v || v === 0) values.add(String(v).trim());
        }
        return Array.from(values).filter((s) => s.trim() !== '');
    }, [fetchInvestors]);

    const listInvestorAUMCurrencies = useCallback(async () => {
        const res = await fetchInvestors();
        const data = Array.isArray(res) ? res : res?.data ?? [];
        if (!Array.isArray(data)) return [];
        const keys = Object.keys(data[0] ?? {});
        // prefer explicit AUM currency keys, otherwise fall back to any currency-like key
        const currencyKey = keys.find((k) => /aum.*currency/i.test(k) || /aum_currency/i.test(k) || /aum\b/i.test(k))
            || keys.find((k) => /aum/i.test(k))
            || keys.find((k) => /currency/i.test(k));
        const values = new Set<string>();
        for (const r of data) {
            const v = currencyKey
                ? r[currencyKey]
                : (r['Investor AUM Currency'] ??  null);
            if (v || v === 0) values.add(String(v).trim());
        }
        return Array.from(values).filter((s) => s.trim() !== '');
    }, [fetchInvestors]);

    return {
        fetchInvestors,
        fetchContacts,
        fetchContactsByInvestor,
        fetchPeople,
        listInvestorTypes,
        listInvestmentRegions,
        listInvestorAUMCurrencies,
    } as const;
}
